import type { ButtonDefaultProps } from '@leafwell/components';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@leafwell/components';
import Image from 'next/image';

import { useWordPressContext } from 'contexts/wordpress-provider';
import { usePaginationUrl } from '../hooks/usePaginationUrl';

type PaginationVariant = 'default' | 'minimal';

const PaginationButton: React.FC<
  Omit<ButtonDefaultProps, 'onClick' | 'variant'> & {
    dataLoading?: boolean;
    isActive?: boolean;
    onClick: () => void;
    variant?: PaginationVariant;
  }
> = memo(
  ({
    dataLoading = false,
    isActive = false,
    disabled = false,
    onClick,
    variant = 'default',
    ...props
  }) => {
    const isMinimal = variant === 'minimal';
    const [loading, toggleLoading] = useState(false);

    useEffect(() => {
      if (!dataLoading) {
        toggleLoading(dataLoading);
      }
    }, [dataLoading]);

    const buttonClassNames = useMemo(() => {
      switch (variant) {
        case 'minimal':
          return isActive
            ? 'pointer-events-none font-medium !border-transparent !text-black'
            : '!text-gray-400 hover:!text-white';
        default:
          return isActive
            ? 'pointer-events-none bg-lilac-100'
            : '!border-indigo-200';
      }
    }, [isActive, variant]);

    return (
      <li
        className={[
          'animate-fade-in transition-opacity',
          disabled ? 'w-0 overflow-hidden opacity-0' : '',
        ].join(' ')}
      >
        <Button
          {...{
            ...props,
            className: [
              'animate-fade-in',
              'group p-reset inline-grid items-center w-8 h-8 text-sm font-sans rounded-full',
              buttonClassNames,
            ].join(' '),
            disabled,
            filled: isActive && !isMinimal,
            loading: loading && dataLoading,
            onClick: () => {
              toggleLoading(true);
              onClick();
            },
            size: 'small',
            variant: isMinimal ? 'light' : 'secondary',
          }}
        />
      </li>
    );
  },
);

const PaginationArrow: React.FC<{
  prev?: boolean;
}> = memo(({ prev = false }) => {
  const { translations } = useWordPressContext();

  return (
    <Image
      src="/arrow.svg"
      alt={prev ? translations['Previous'] : translations['Next']}
      height={12}
      width={15}
      className={[
        'grayscale group-hover:invert',
        prev ? 'rotate-180' : '',
      ].join(' ')}
    />
  );
});

const PaginationDots: React.FC = memo(() => (
  <li className="animate-fade-in" aria-hidden="true">
    &#8230;
  </li>
));

const DOT_INDICATOR = 'DOTS';

type PagesRange = (typeof DOT_INDICATOR | number)[];

type PagesData = {
  currentPage: number;
  pagesRange: PagesRange;
  totalPages: number;
};

export type PaginationProps = {
  className?: string;
  limit: number;
  loading?: boolean;
  offset?: number;
  onPageChange: (offset?: number, page?: number) => void;
  siblingCount?: number;
  updateUrls?: boolean;
  total: number;
  variant?: PaginationVariant;
};

const Pagination: React.FC<PaginationProps> = ({
  className,
  limit,
  loading: dataLoading,
  offset = 0,
  onPageChange,
  siblingCount = 1,
  updateUrls = false,
  total,
  variant = 'default',
}) => {
  const updatePageUrl = usePaginationUrl();

  const getPagesRange = useCallback((start: number, end: number): number[] => {
    const length = end - start + 1;

    return Array.from({ length }, (_, idx) => idx + start);
  }, []);

  const handleClick = useCallback(
    (page: number): void => {
      onPageChange((page - 1) * limit, page);
    },
    [limit],
  );

  const { currentPage, pagesRange, totalPages } = useMemo<PagesData>(() => {
    const currentPage = Math.floor(offset / limit) + 1;
    const totalPages = Math.ceil(total / limit);
    const firstButton =
      currentPage - siblingCount > 1 ? currentPage - siblingCount : 1;
    const lastButton =
      currentPage + siblingCount < totalPages
        ? currentPage + siblingCount
        : totalPages;

    let pagesRange: PagesRange = getPagesRange(firstButton, lastButton);

    if (!pagesRange.includes(1)) {
      pagesRange = [
        1,
        1 < currentPage - siblingCount * 2 ? DOT_INDICATOR : null,
        ...pagesRange,
      ];
    }

    if (!pagesRange.includes(totalPages)) {
      pagesRange = [
        ...pagesRange,
        totalPages > siblingCount * 2 + currentPage ? DOT_INDICATOR : null,
        totalPages,
      ];
    }

    if (updateUrls) {
      updatePageUrl(currentPage);
    }

    return {
      currentPage,
      pagesRange: pagesRange.filter(Boolean),
      totalPages,
    };
  }, [limit, offset, updateUrls, siblingCount, total]);

  if (totalPages === 1) return null;

  return (
    <ul
      className={['grid grid-flow-col justify-start gap-x-2', className].join(
        ' ',
      )}
    >
      <PaginationButton
        {...{
          disabled: currentPage <= 1,
          dataLoading,
          label: <PaginationArrow prev />,
          onClick: () => handleClick(currentPage - 1),
          variant,
        }}
      />
      {pagesRange.map((page, index) => {
        // If the pageItem is a DOT, render the DOTS unicode character
        if (page === DOT_INDICATOR) {
          return <PaginationDots key={index} />;
        }

        return (
          <PaginationButton
            {...{
              key: index,
              dataLoading,
              label: page,
              isActive: currentPage === page,
              onClick: () => handleClick(page),
              variant,
            }}
          />
        );
      })}
      <PaginationButton
        {...{
          disabled: currentPage >= totalPages,
          dataLoading,
          label: <PaginationArrow />,
          onClick: () => handleClick(currentPage + 1),
          variant,
        }}
      />
    </ul>
  );
};

export default Pagination;
